import { useState } from 'react';

import EBTCardSummary from 'features/shop/cart/EBTPaymentFlow/shared/EBTCardSummary';
import { StepButton, StepCard, StepHeading } from 'features/shop/cart/EBTPaymentFlow/shared/FlowPresentation';
import { EBTFormSubmissionError } from 'shared/ebt/EBTErrors';
import ForagePinElement from 'shared/ebt/ForagePinElement';
import useEBTBalanceCheck from 'shared/ebt/useEBTBalanceCheck';

/**
 * CheckBalance component handles the EBT balance checking step in the payment flow.
 *
 * @param {Object} props - The component props
 * @param {Object} props.ebtCard - EBT card information
 * @param {string} props.ebtCard.last4 - Last 4 digits of the EBT card
 * @param {Object} props.ebtCard.paymentMethodRef - Payment method reference for the EBT card
 * @param {Object} props.flowData - Current flow state data
 * @param {number|null} props.flowData.balance - Current EBT balance (snap and cash amounts)
 * @param {Function} props.handleNext - Callback function to proceed to the next step
 * @param {Function} props.setFlowData - Function to update the flow data state
 *
 * @returns {JSX.Element} The rendered EBT balance check form
 */
function CheckBalance({ ebtCard, flowData, handleNext, setFlowData }) {
    const { balanceCheck, balanceCheckError, isBalanceLoading } = useEBTBalanceCheck(ebtCard?.last4);

    const [pinField, setPinField] = useState({
        element: null,
        isValid: false,
        showFieldError: false,
    });

    const handlePinChange = (_, isValid) => {
        setPinField((prev) => ({ ...prev, isValid, showFieldError: false }));
    };

    const handlePinReady = (element) => {
        setPinField((prev) => ({ ...prev, element, showFieldError: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!pinField.isValid) {
            setPinField((prev) => ({ ...prev, showFieldError: true }));
            return;
        }

        await balanceCheck(pinField.element, ebtCard?.paymentMethodRef, (balance) => {
            setFlowData({ ...flowData, balance });
            handleNext(flowData.previousStep, { enableBalanceCheck: false, focusAmountField: true });
        });
    };

    return (
        <div className="flex flex-col gap-15">
            <StepHeading>Check Balance</StepHeading>
            <StepCard>
                <form onSubmit={handleSubmit}>
                    <EBTCardSummary last4={ebtCard?.last4} className="mb-30" renderBalance={false} />
                    <ForagePinElement
                        elementID="edit-payment-balance-check-pin-element"
                        onChange={handlePinChange}
                        onReady={handlePinReady}
                        error={pinField.showFieldError}
                    />
                    <StepButton
                        disabled={isBalanceLoading}
                        loading={isBalanceLoading}
                        loadingText="Checking Balance..."
                    >
                        Check Balance
                    </StepButton>
                    {balanceCheckError && <EBTFormSubmissionError className="text-center" error={balanceCheckError} />}
                    <p className="mt-10 text-center">
                        <button onClick={() => handleNext(flowData.previousStep)} className="link" type="button">
                            Cancel
                        </button>
                    </p>
                </form>
            </StepCard>
        </div>
    );
}

export default CheckBalance;
